<template>
  <div class="modern-master-view pb-12">
    <!-- Premium Global Header -->
    <v-row class="mb-8 align-center px-4 pt-4">
      <div class="d-flex align-center">
        <v-avatar color="primary" size="48" class="mr-4 shadow-sm border-white-sm">
          <v-icon color="white">fas fa-file-invoice-dollar</v-icon>
        </v-avatar>
        <div>
          <div class="text-h4 font-weight-black primary--text lh-1">Documentos Emitidos</div>
          <div class="caption grey--text font-weight-bold uppercase tracking-widest mt-1">Gestión de documentos tributarios y cobranza</div>
        </div>
      </div>
      <v-spacer></v-spacer>
      <div class="d-flex align-center gap-3">
        <export-invoices
          :filter="filter"
          :options="options"
          :active-tab="activeTab"
          :search="search"
          :disabled="loading"
        />
        <Can I="create" a="Invoice">
          <v-btn
            v-if="facturacionClEnabled"
            outlined
            color="primary"
            class="font-weight-black text-uppercase tracking-widest rounded-lg px-6 h-44 mr-3"
            @click="$router.push({ name: 'BulkDispatch' })"
          >
            <v-icon left small>fas fa-truck-ramp-box</v-icon> Guías masivas
          </v-btn>
          <div class="d-flex align-center">
            <v-btn 
              color="success" 
              class="font-weight-black text-uppercase tracking-widest rounded-lg px-8 h-44 shadow-sm" 
              @click="$router.push({name:'CreateDTE',params:{id:-1,tipo: (simpleFacturaEnabled || facturacionClEnabled) ? 33 : 30}})"
            >
              <v-icon left small>fas fa-plus</v-icon> Emitir DTE
            </v-btn>
          </div>
        </Can>
      </div>
    </v-row>

    <!-- Metrics Cards Section -->
    <v-row class="mb-8 px-4">
      <v-col cols="12" md="6">
        <v-card outlined class="modern-card pa-6 d-flex align-center border-left-primary">
          <v-avatar color="primary lighten-5" size="48" class="mr-4">
            <v-icon color="primary">fas fa-calculator</v-icon>
          </v-avatar>
          <div>
            <div class="caption font-weight-black text-uppercase tracking-widest grey--text mb-1">Total Facturado (c/ IVA)</div>
            <div class="text-h5 font-weight-black primary--text">{{ total | moneda }}</div>
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" md="6">
        <v-card outlined class="modern-card pa-6 d-flex align-center border-left-success">
          <v-avatar color="success lighten-5" size="48" class="mr-4">
            <v-icon color="success">fas fa-hand-holding-usd</v-icon>
          </v-avatar>
          <div class="flex-grow-1">
            <div class="caption font-weight-black text-uppercase tracking-widest grey--text mb-1">Total Percibido / Pagado</div>
            <div class="d-flex align-center justify-space-between">
              <div class="text-h5 font-weight-black success--text">{{ payed | moneda }}</div>
              <v-chip x-small color="success lighten-4" class="success--text font-weight-black px-2">
                {{ (payed / total) | percent }} del total
              </v-chip>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Category Tabs -->
    <div class="px-4 mb-4">
      <v-tabs
        v-model="activeTab"
        background-color="white"
        color="primary"
        class="rounded-xl shadow-sm border-subtle overflow-hidden modern-tabs"
        @change="onTabChange"
        show-arrows
      >
        <v-tab href="#all" class="font-weight-black text-none px-6">
          <v-icon left small>fas fa-layer-group</v-icon>
          Todos los Documentos
        </v-tab>
        <v-tab href="#facturas" class="font-weight-black text-none px-6">
          <v-icon left small>fas fa-file-invoice</v-icon>
          Facturas
        </v-tab>
        <v-tab href="#boletas" class="font-weight-black text-none px-6">
          <v-icon left small>fas fa-receipt</v-icon>
          Boletas
        </v-tab>
        <v-tab href="#guias" class="font-weight-black text-none px-6">
          <v-icon left small>fas fa-truck</v-icon>
          Guías de Despacho
        </v-tab>
        <v-tab href="#nc" class="font-weight-black text-none px-6">
          <v-icon left small>fas fa-file-invoice-dollar</v-icon>
          Notas de Crédito
        </v-tab>
      </v-tabs>
    </div>

    <!-- Filters Section -->
    <v-card outlined class="modern-card pa-6 mb-8 shadow-sm">
      <v-row dense align="center">
        <v-col cols="12" sm="6" md="2">
          <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Desde</div>
          <v-text-field 
            v-model='filter.created_after' 
            outlined dense hide-details clearable type="date"
            class="modern-input"
            @change="()=>{ options.page=1; fetch() }"
          />
        </v-col>
        <v-col cols="12" sm="6" md="2">
          <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Hasta</div>
          <v-text-field 
            v-model='filter.created_before' 
            outlined dense hide-details clearable type="date"
            class="modern-input"
            @change="()=>{ options.page=1; fetch() }"
          />
        </v-col>
        <v-col cols="12" sm="6" md="2">
          <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Origen</div>
          <v-select
            v-model="filter.source"
            :items="sourceOptions"
            item-text="text"
            item-value="value"
            outlined dense hide-details
            class="modern-input"
            @change="()=>{ options.page=1; fetch() }"
          />
        </v-col>
        <v-col cols="12" sm="6" md="2">
          <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Tipo</div>
          <v-select
            v-model="filter.tipo"
            :items="tipoOptions"
            item-text="text"
            item-value="value"
            placeholder="Todos"
            outlined dense hide-details clearable
            class="modern-input"
            @change="()=>{ options.page=1; fetch() }"
          />
        </v-col>
        <v-col v-if="activeTab === 'guias' && facturacionClEnabled" cols="12" sm="6" md="2">
          <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Guías masivas</div>
          <v-select
            v-model="filter.dispatch_batch"
            :items="dispatchBatchOptions"
            item-text="text"
            item-value="value"
            outlined dense hide-details
            class="modern-input"
            @change="()=>{ options.page=1; fetch() }"
          />
        </v-col>
        <v-col cols="12" sm="6" md="2">
          <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Vendedor</div>
          <v-select
            v-model="filter.seller"
            :items="sellers"
            item-text="email"
            item-value="id"
            placeholder="Todos"
            outlined dense hide-details clearable
            class="modern-input"
            @change="()=>{ options.page=1; fetch() }"
          />
        </v-col>
        <v-col cols="12" sm="6" md="2">
          <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Estatus</div>
          <v-select
            v-model="filter.estatus"
            :items="estatusFilterOptions"
            item-text="text"
            item-value="value"
            placeholder="Todos"
            outlined dense hide-details clearable
            class="modern-input"
            @change="()=>{ options.page=1; fetch() }"
          />
        </v-col>
        <v-col cols="12" md="auto" class="d-flex align-end pb-1 ml-auto">
          <v-btn color="primary" icon class="grey lighten-4" @click="fetch" :loading="loading">
            <v-icon small>fas fa-sync</v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <!-- Main Table Card -->
    <v-card outlined class="modern-card overflow-hidden shadow-sm">
      <v-data-table
        :headers="computedHeaders"
        :items="dtes"
        :options.sync="options"
        :server-items-length="totalDtes"
        :loading="loading"
        must-sort
        class="modern-table"
        :item-class="(item)=>{
          if (item.estatus == 3) return 'row-style-annulled'
          if (item.tipo==60 || item.tipo==61 || item.sf_tipo==61) return 'row-style-nc'
          return 'row-style-normal'
        }"
        @click:row='displayDetails'
        @update:options="fetch"
        :footer-props="{
          showFirstLastPage: true,
          itemsPerPageOptions: [10, 20, 50, 100],
        }"
      >
        <!-- Integrated Search Slot -->
        <template v-slot:top>
          <div class="px-6 py-4 border-bottom bg-lightgrey d-flex align-center">
            <span class="caption font-weight-black uppercase tracking-widest grey--text">Documentos Emitidos: {{totalDtes}}</span>
            <v-spacer></v-spacer>
            <v-text-field
              v-model="search"
              @keyup="searchTimeOut"
              placeholder="Buscar por folio, RUT o cliente..."
              prepend-inner-icon="fas fa-search"
              outlined dense hide-details clearable
              :disabled="loading"
              class="modern-search-field"
            ></v-text-field>
          </div>
        </template>

        <!-- Cell Templates -->
        <template v-slot:item.document_id="{ item }">
          <div class="d-flex flex-column py-2">
            <!-- Header: Folio & Estatus -->
            <div class="d-flex align-center mb-1">
              <!-- Si es Nota de Crédito -->
              <template v-if="isCreditNote(item)">
                <span class="body-1 font-weight-black error--text mr-2">NC #{{ item.document_id || item.folio || '-' }}</span>
                <v-chip
                  x-small
                  color="error lighten-5"
                  class="error--text font-weight-black"
                  label
                >
                  Nota de Crédito
                </v-chip>
              </template>

              <!-- Si es Documento Anulado -->
              <template v-else-if="item.estatus == 3">
                <span class="body-1 font-weight-black text-decoration-line-through grey--text text--darken-2 mr-2">#{{ item.document_id || item.folio || '-' }}</span>
                <v-chip
                  x-small
                  color="error"
                  class="white--text font-weight-black"
                  label
                >
                  <v-icon x-small left color="white">fas fa-ban</v-icon>
                  ANULADO
                </v-chip>
              </template>

              <!-- Documento Normal -->
              <template v-else>
                <span class="body-1 font-weight-black primary--text mr-2">#{{ item.document_id || item.folio || '-' }}</span>
                <v-chip
                  x-small
                  :color="getStatusColor(item) + ' lighten-5'"
                  :class="getStatusColor(item) + '--text font-weight-black'"
                  label
                >
                  {{ getStatusName(item) }}
                </v-chip>
                <v-chip
                  v-if="item.dispatch_batch"
                  x-small label outlined color="primary" class="ml-1 font-weight-bold"
                  :title="'Emitida desde guías masivas · Lote #' + item.dispatch_batch + (item.dispatch_batch_name ? ' · ' + item.dispatch_batch_name : '')"
                  @click.stop="$router.push({ name: 'BulkDispatchBatch', params: { batchId: item.dispatch_batch } })"
                >
                  <v-icon x-small left>fas fa-truck-ramp-box</v-icon>
                  <template v-if="item.dispatch_batch_name">{{ item.dispatch_batch_name.length > 18 ? item.dispatch_batch_name.slice(0, 18) + '…' : item.dispatch_batch_name }}</template>
                  <template v-else>Lote #{{ item.dispatch_batch }}</template>
                </v-chip>
              </template>
            </div>

            <!-- Para Notas de Crédito: Documento referenciado en pequeño + Motivo -->
            <div v-if="isCreditNote(item)" class="d-flex flex-column my-1">
              <div
                v-if="item.referencia_folio"
                class="caption font-weight-bold grey--text text--darken-3 d-flex align-center cursor-pointer hover-link"
                :title="`Buscar documento referenciado #${item.referencia_folio}`"
                @click.stop="openReferencedDoc(item)"
              >
                <v-icon x-small color="grey darken-2" class="mr-1">fas fa-link</v-icon>
                <span>Ref: {{ getReferenciaDocLabel(item) }} #{{ item.referencia_folio }}</span>
              </div>
              <div v-if="item.motivo_display" class="mt-1">
                <v-chip x-small color="amber lighten-5" class="amber--text text--darken-4 font-weight-black" label>
                  <v-icon x-small left color="amber darken-4">fas fa-info-circle</v-icon>
                  {{ item.motivo_display }}
                </v-chip>
              </div>
            </div>

            <!-- Para Documentos Anulados: Mostrar NC Asociada -->
            <div v-else-if="item.estatus == 3 && item.nc_reference" class="my-1">
              <v-chip
                x-small
                outlined
                color="error"
                class="font-weight-black cursor-pointer bg-white"
                :title="`Ver Nota de Crédito #${item.nc_reference.folio}`"
                @click.stop="openAssociatedNc(item.nc_reference)"
              >
                <v-icon x-small left color="error">fas fa-undo</v-icon>
                NC #{{ item.nc_reference.folio }} ({{ item.nc_reference.motivo_display }})
              </v-chip>
            </div>

            <!-- Para Facturas / Otros DTEs con referencia (ej. OC o Guía) -->
            <div v-else-if="item.referencia_folio" class="my-1">
              <v-chip
                x-small
                :color="(Number(item.referencia_tipo) === 50 || Number(item.referencia_tipo) === 52 ? 'info' : 'primary') + ' lighten-5'"
                :class="(Number(item.referencia_tipo) === 50 || Number(item.referencia_tipo) === 52 ? 'info' : 'primary') + '--text text--darken-3 font-weight-black cursor-pointer'"
                :title="`Buscar ${getReferenciaDocLabel(item)} #${item.referencia_folio}`"
                @click.stop="openReferencedDoc(item)"
              >
                <v-icon x-small left :color="(Number(item.referencia_tipo) === 50 || Number(item.referencia_tipo) === 52 ? 'info darken-2' : 'primary')">
                  {{ (Number(item.referencia_tipo) === 50 || Number(item.referencia_tipo) === 52) ? 'fas fa-truck' : 'fas fa-link' }}
                </v-icon>
                <span>Ref: {{ getReferenciaDocLabel(item) }} #{{ item.referencia_folio }}</span>
              </v-chip>
            </div>

            <!-- Para Guías de Despacho con Factura(s) Relacionada(s) -->
            <div v-else-if="isGuia(item) && item.related_invoices && item.related_invoices.length > 0" class="my-1">
              <v-chip
                v-for="(inv, idx) in item.related_invoices"
                :key="'col-rel-' + idx"
                x-small
                color="success lighten-5"
                class="success--text text--darken-3 font-weight-black cursor-pointer mr-1"
                @click.stop="openRelatedInvoice(inv)"
                :title="`Ver Factura #${inv.folio || inv.document_id}`"
              >
                <v-icon x-small left color="success darken-2">fas fa-file-invoice-dollar</v-icon>
                Factura #{{ inv.folio || inv.document_id }}
              </v-chip>
            </div>

            <!-- Badges: Tipo y Origen -->
            <div class="d-flex align-center flex-wrap mt-1">
              <v-chip
                x-small
                :color="getTypeChipColor(item) + ' lighten-5'"
                :class="getTypeChipColor(item) + '--text font-weight-black mr-2'"
                label
              >
                {{ getTypeChipLabel(item) }}
              </v-chip>
              <v-chip
                v-if="item.source === 'simplefactura'"
                x-small
                color="teal lighten-5"
                class="teal--text text--darken-2 font-weight-black"
                label
                title="Emitido con Simple Factura (SII)"
              >
                <v-icon x-small left color="teal darken-2">fas fa-cloud</v-icon>
                Simple Factura
              </v-chip>
              <v-chip
                v-else-if="item.source === 'facturacioncl'"
                x-small
                color="deep-purple lighten-5"
                class="deep-purple--text text--darken-2 font-weight-black"
                label
                title="Emitido con Facturación.cl (SII)"
              >
                <v-icon x-small left color="deep-purple darken-2">fas fa-file-invoice</v-icon>
                Facturación.cl
              </v-chip>
              <v-chip
                v-else
                x-small
                color="grey lighten-4"
                class="grey--text text--darken-1 font-weight-bold"
                label
                title="Documento local / histórico"
              >
                Local
              </v-chip>
            </div>
          </div>
        </template>

        <!-- Slot Referencia para pestañas NC y Facturas -->
        <template v-slot:item.referencia="{ item }">
          <div v-if="item.referencia_folio" class="d-flex align-center py-2 cursor-pointer hover-link" @click.stop="openReferencedDoc(item)">
            <v-chip
              x-small
              :color="(Number(item.referencia_tipo) === 50 || Number(item.referencia_tipo) === 52 ? 'info' : 'primary') + ' lighten-5'"
              :class="(Number(item.referencia_tipo) === 50 || Number(item.referencia_tipo) === 52 ? 'info' : 'primary') + '--text font-weight-black mr-1'"
              label
            >
              <v-icon x-small left>{{ (Number(item.referencia_tipo) === 50 || Number(item.referencia_tipo) === 52) ? 'fas fa-truck' : 'fas fa-link' }}</v-icon>
              {{ getReferenciaDocLabel(item) }}
            </v-chip>
            <span class="body-2 font-weight-black primary--text">#{{ item.referencia_folio }}</span>
          </div>
          <span v-else class="caption grey--text opacity-40">-</span>
        </template>

        <!-- Slot Factura(s) Relacionada(s) para pestaña Guías -->
        <template v-slot:item.related_invoices="{ item }">
          <div v-if="item.related_invoices && item.related_invoices.length > 0" class="py-2 d-flex flex-wrap align-center">
            <v-chip
              v-for="(inv, idx) in item.related_invoices"
              :key="'rel-col-' + idx"
              x-small
              color="success lighten-5"
              class="success--text text--darken-3 font-weight-black cursor-pointer mr-1 mb-1"
              @click.stop="openRelatedInvoice(inv)"
              :title="`Ver Factura #${inv.folio || inv.document_id} · Total: ${inv.total || 0}`"
            >
              <v-icon x-small left color="success darken-2">fas fa-file-invoice-dollar</v-icon>
              Factura #{{ inv.folio || inv.document_id }}
            </v-chip>
          </div>
          <span v-else class="caption grey--text text--darken-1 font-italic">Sin facturar</span>
        </template>

        <!-- Slot Motivo para pestaña NC -->
        <template v-slot:item.motivo_display="{ item }">
          <div v-if="item.motivo_display" class="py-2">
            <v-chip x-small color="amber lighten-5" class="amber--text text--darken-4 font-weight-black" label>
              <v-icon x-small left color="amber darken-4">fas fa-tag</v-icon>
              {{ item.motivo_display }}
            </v-chip>
          </div>
          <span v-else class="caption grey--text opacity-40">-</span>
        </template>

        <template v-slot:item.date_emited="{ item }">
          <span class="body-2 grey--text text--darken-3 font-weight-medium">{{ item.date_emited | moment('DD/MM/YYYY') }}</span>
        </template>

        <template v-slot:item.payment_date="{ item }">
          <span class="caption grey--text font-weight-bold">{{ item.payment_date | moment('DD/MM/YYYY') }}</span>
        </template>

        <template v-slot:item.rznSocRecep="{ item }">
          <div class="d-flex flex-column py-2">
            <span class="body-2 font-weight-bold grey--text text--darken-4 lh-1-2 mb-1">{{ item.rznSocRecep }}</span>
            <span class="caption grey--text font-weight-black opacity-60">{{ item.rutRecep }}</span>
          </div>
        </template>

        <template v-slot:item.work_order="{ item }">
          <v-chip v-if="item.work_order" x-small outlined color="primary" class="font-weight-black">OT #{{ item.work_order }}</v-chip>
          <span v-else class="caption grey--text opacity-40">-</span>
        </template>

        <template v-slot:item.sales_note="{ item }">
          <span v-if="item.sales_note" class="caption grey--text font-weight-bold">CN #{{ item.sales_note }}</span>
          <span v-else class="caption grey--text opacity-40">-</span>
        </template>

        <template v-slot:item.get_total="{ item }">
          <span class="body-1 font-weight-black primary--text tracking-tighter">{{ item.get_total | moneda }}</span>
        </template>

        <template v-slot:item.payed="{ item }">
          <div v-if="item.tipo==60 || item.tipo==61" class="text-center caption grey--text">-</div>
          <div v-else class="d-flex flex-column align-end">
            <span :class="(item.get_total - item.get_payed) > 0 ? 'error--text' : 'success--text'" class="body-2 font-weight-black">
              {{ item.get_payed | moneda }}
            </span>
            <span v-if="(item.get_total - item.get_payed) > 0" class="caption grey--text opacity-60">Pend: {{ (item.get_total - item.get_payed) | moneda }}</span>
          </div>
        </template>

        <template v-slot:item.actions="{ item }">
          <div class="d-flex align-center justify-end">
            <!-- Botón directo Facturar Guía / Facturada -->
            <Can I="create" a="Invoice">
              <v-btn
                v-if="isGuia(item) && item.related_invoices && item.related_invoices.length > 0"
                x-small
                outlined
                color="success"
                class="rounded font-weight-black mr-2 text-none"
                @click.stop="openRelatedInvoice(item.related_invoices[0])"
                :title="`Ver Factura #${item.related_invoices[0].folio || item.related_invoices[0].document_id}`"
              >
                <v-icon x-small left color="success">fas fa-check-circle</v-icon>
                Facturada (#{{ item.related_invoices[0].folio || item.related_invoices[0].document_id }})
              </v-btn>
              <v-btn
                v-else-if="isGuia(item)"
                x-small
                color="success"
                depressed
                class="rounded font-weight-black mr-2 text-none"
                @click.stop="facturarGuia(item)"
                title="Emitir Factura referenciando esta Guía"
              >
                <v-icon x-small left>fas fa-file-invoice-dollar</v-icon>
                Facturar
              </v-btn>
            </Can>

            <v-menu bottom left offset-y transition="slide-y-transition" content-class="modern-card">
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon v-bind="attrs" v-on="on" class="grey lighten-4" @click.stop>
                  <v-icon small color="grey darken-2">fas fa-ellipsis-vertical</v-icon>
                </v-btn>
              </template>
              <v-list dense class="py-1">
                <Can I="create" a="Invoice">
                  <template v-if="isGuia(item) && item.related_invoices && item.related_invoices.length > 0">
                    <v-list-item
                      v-for="(relInv, rIdx) in item.related_invoices"
                      :key="'menu-rel-' + rIdx"
                      @click.stop="openRelatedInvoice(relInv)"
                    >
                      <v-list-item-icon class="mr-2">
                        <v-icon x-small color="success">fas fa-external-link-alt</v-icon>
                      </v-list-item-icon>
                      <v-list-item-title class="caption font-weight-bold success--text text--darken-2">
                        Ver Factura #{{ relInv.folio || relInv.document_id }}
                      </v-list-item-title>
                    </v-list-item>
                    <v-list-item @click.stop="facturarGuia(item)">
                      <v-list-item-icon class="mr-2">
                        <v-icon x-small color="primary">fas fa-plus</v-icon>
                      </v-list-item-icon>
                      <v-list-item-title class="caption font-weight-bold primary--text">
                        Facturar nuevamente
                      </v-list-item-title>
                    </v-list-item>
                  </template>
                  <v-list-item v-else-if="isGuia(item)" @click.stop="facturarGuia(item)">
                    <v-list-item-icon class="mr-2">
                      <v-icon x-small color="success">fas fa-file-invoice-dollar</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title class="caption font-weight-bold success--text text--darken-1">Facturar Guía</v-list-item-title>
                  </v-list-item>
                </Can>

                <v-divider v-if="isGuia(item)" class="my-1"></v-divider>

                <v-list-item v-if="item.source==='simplefactura'" @click.stop="openSimpleFacturaPdf(item)">
                  <v-list-item-icon class="mr-2">
                    <v-icon x-small color="error">fas fa-file-pdf</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title class="caption font-weight-bold">Ver PDF Oficial SII</v-list-item-title>
                </v-list-item>

                <v-list-item v-if="item.source==='facturacioncl'" @click.stop="openFacturacionClPdf(item)">
                  <v-list-item-icon class="mr-2">
                    <v-icon x-small color="error">fas fa-file-pdf</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title class="caption font-weight-bold">Ver PDF Facturación.cl</v-list-item-title>
                </v-list-item>

                <v-list-item @click.stop="openDetail(item)">
                  <v-list-item-icon class="mr-2">
                    <v-icon x-small color="primary">fas fa-eye</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title class="caption font-weight-bold">Ver Detalle Interno</v-list-item-title>
                </v-list-item>

                <Can I="update" a="Invoice">
                  <v-list-item :disabled="item.tipo==60 || item.tipo==61 || !Number(item.id)" @click.stop="()=>{ itemToPayment=item; toPayment=true; }">
                    <v-list-item-icon class="mr-2">
                      <v-icon x-small color="success">fas fa-money-check-alt</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title class="caption font-weight-bold">Registrar Pago</v-list-item-title>
                  </v-list-item>
                </Can>

                <template v-if="canIssueCreditNote(item)">
                  <v-divider class="my-1"></v-divider>
                  <v-subheader class="caption font-weight-black grey--text px-4">Nota de crédito</v-subheader>
                  <v-list-item
                    v-for="motivo in ncMotivos"
                    :key="'nc-' + (item.sf_id || item.id) + '-' + motivo.value"
                    @click.stop="openCreditNote(item, motivo)"
                  >
                    <v-list-item-icon class="mr-2">
                      <v-icon x-small color="error">fas fa-file-invoice</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title class="caption font-weight-bold">{{ motivo.text }}</v-list-item-title>
                  </v-list-item>
                </template>
                
                <v-divider v-if="item.source!=='simplefactura' && item.source!=='facturacioncl'" class="my-1"></v-divider>

                <Can I="update" a="Invoice">
                  <v-list-item v-if="Boolean(item.estatus<2) && item.source!=='simplefactura' && item.source!=='facturacioncl'" @click.stop="()=>{ invoice=item; showConfirmReceived=true; }">
                    <v-list-item-icon class="mr-2">
                      <v-icon x-small color="primary">fas fa-sync</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title class="caption font-weight-bold">Recepcionar</v-list-item-title>
                  </v-list-item>
                </Can>

                <Can I="delete" a="Invoice">
                  <v-list-item v-if="Boolean(item.estatus<3) && item.source!=='simplefactura' && item.source!=='facturacioncl'" @click.stop="()=>{ invoice=item; showConfirmCancel=true; }" class="error--text">
                    <v-list-item-icon class="mr-2">
                      <v-icon x-small color="error">fas fa-trash</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title class="caption font-weight-bold">Anular Factura</v-list-item-title>
                  </v-list-item>
                </Can>
              </v-list>
            </v-menu>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Dialogs -->
    <confirm
      v-if="invoice"
      v-model="showConfirmCancel" 
      :message="`¿Confirma la anulación de la Factura #${invoice.document_id}?`" 
      @ok="updateEstatus(3)"
      @cancel="showConfirmCancel=false"
    />
    <confirm
      v-if="invoice"
      v-model="showConfirmReceived" 
      :message="`¿Confirma el cambio de Estatus de la Factura #${invoice.document_id} a Recepcionada?`" 
      @ok="updateEstatus(2)"
      @cancel="showConfirmReceived=false"
    />
    
    <v-dialog v-model="showDetail" max-width="1200" content-class="modern-dialog">
      <v-card class="overflow-hidden rounded-xl bg-white">
        <invoice-detail
          :invoice="detailToDisplay"
          @close="showDetail=false"
          @bill-guia="(item) => { showDetail=false; facturarGuia(item); }"
          @search-doc="(docFolio) => { showDetail=false; searchByFolio(docFolio); }"
        />
      </v-card>
    </v-dialog>

    <v-dialog v-model="toPayment" max-width="900" content-class="modern-dialog" persistent>
      <v-card class="overflow-hidden rounded-xl bg-white">
        <div class="pa-8">
          <div class="d-flex align-center mb-6">
            <v-avatar color="success lighten-5" size="40" class="mr-4">
              <v-icon color="success" size="18">fas fa-money-bill-wave</v-icon>
            </v-avatar>
            <h2 class="text-h5 font-weight-black primary--text">Historial de Pagos - Factura #{{ itemToPayment ? itemToPayment.document_id : '' }}</h2>
            <v-spacer></v-spacer>
            <v-btn icon @click="toPayment=false"><v-icon>fas fa-times</v-icon></v-btn>
          </div>
          <invoice-pay-detail :invoice="itemToPayment" @refresh="()=>{ toPayment=false; fetch(); }"/>
        </div>
      </v-card>
    </v-dialog>

    <v-dialog v-model="ncDialog" max-width="920" content-class="modern-dialog" persistent>
      <v-card v-if="ncSource" class="overflow-hidden rounded-xl bg-white">
        <div class="pa-8">
          <div class="d-flex align-center mb-6">
            <v-avatar color="error lighten-5" size="40" class="mr-4">
              <v-icon color="error" size="18">fas fa-file-invoice</v-icon>
            </v-avatar>
            <div>
              <h2 class="text-h5 font-weight-black primary--text">Nota de crédito · {{ ncMotivoLabel }}</h2>
              <div class="caption grey--text">Referencia folio #{{ ncSource.document_id }} · {{ ncSource.rznSocRecep }}</div>
            </div>
            <v-spacer></v-spacer>
            <v-btn icon :disabled="ncSending" @click="ncDialog=false"><v-icon>fas fa-times</v-icon></v-btn>
          </div>

          <v-alert v-if="ncMotivo === 1" dense text type="info" class="mb-4">
            Se emitirá una NC que anula el documento completo, con las mismas líneas y montos.
          </v-alert>
          <v-alert v-else-if="ncMotivo === 2" dense text type="info" class="mb-4">
            Corrija el texto de las líneas. La nota se emite sin montos (el SII no los permite al corregir texto).
          </v-alert>
          <v-alert v-else dense text type="info" class="mb-4">
            Indique el monto a acreditar por línea (la diferencia a descontar, no el valor corregido). No puede superar el valor del documento original ({{ ncOriginalTotal | moneda }}).
          </v-alert>

          <v-data-table
            :headers="ncHeaders"
            :items="ncLines"
            hide-default-footer
            :items-per-page="-1"
            class="elevation-0 mb-4"
          >
            <template v-slot:item.name="{ item }">
              <v-text-field
                v-if="ncMotivo === 2"
                v-model="item.name"
                dense hide-details
                outlined
              />
              <span v-else>{{ item.name }}</span>
            </template>
            <template v-slot:item.qty="{ item }">
              <v-text-field
                v-if="ncMotivo === 3"
                v-model.number="item.qty"
                type="number"
                min="0"
                dense hide-details
                outlined
                style="max-width: 110px;"
              />
              <span v-else>{{ item.qty }}</span>
            </template>
            <template v-slot:item.price="{ item }">
              <v-text-field
                v-if="ncMotivo === 3"
                v-model.number="item.price"
                type="number"
                min="0"
                dense hide-details
                outlined
                style="max-width: 130px;"
              />
              <span v-else>{{ item.price | moneda }}</span>
            </template>
            <template v-slot:item.total="{ item }">
              <span class="font-weight-black">{{ Number(item.qty || 0) * Number(item.price || 0) | moneda }}</span>
            </template>
          </v-data-table>

          <div class="d-flex justify-end mb-6">
            <div v-if="ncMotivo === 2" class="text-right caption grey--text">Sin montos</div>
            <div v-else class="text-right">
              <div class="caption grey--text">Neto {{ ncNeto | moneda }} · IVA {{ ncIvaAmount | moneda }}</div>
              <div class="text-h6 font-weight-black primary--text">Total {{ ncTotal | moneda }}</div>
            </div>
          </div>

          <div class="d-flex justify-end">
            <v-btn text class="mr-3" :disabled="ncSending" @click="ncDialog=false">Cancelar</v-btn>
            <v-btn color="error" class="font-weight-black" :loading="ncSending" @click="submitCreditNote">
              Emitir nota de crédito
            </v-btn>
          </div>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { fetchDTE, updateDTE, getSalesNote } from '@/api/salesNotes'
import { getCustomer } from '@/api/customers'
import { fetchDocuments, mapDocumentToInvoiceRow, downloadDocumentPdf, createDocument, getDocument } from '@/api/simplefactura'
import {
  fetchDocuments as fetchFclDocuments,
  mapDocumentToInvoiceRow as mapFclDocumentToInvoiceRow,
  downloadDocumentPdf as downloadFclDocumentPdf,
  createDocument as createFclDocument,
  getDocument as getFclDocument,
  listDispatchBatches
} from '@/api/facturacioncl'
import { fetchSellers } from '@/api/auth'
import notifications from '@/utils/notifications'
import ExportInvoices from '@/components/sales/ExportInvoices'
import InvoicePayDetail from '@/components/sales/InvoicePayDetail'
import InvoiceDetail from '@/components/sales/InvoiceDetail'
import moment from 'moment'

export default {
  name: 'Invoices',
  components: { ExportInvoices, InvoicePayDetail, InvoiceDetail },
  data() {
    return {
      loading: false,
      showDetail: false,
      detailToDisplay: '',
      dtes: [],
      sellers: [],
      dispatchBatches: [],
      invoice: '',
      timer: null,
      options: {
        sortBy: ['date_emited'],
        sortDesc: [true],
        page: 1,
        itemsPerPage: 20,
      },
      totalDtes: 0,
      showConfirmCancel: false,
      showConfirmReceived: false,
      toPayment: false,
      itemToPayment: null,
      ncDialog: false,
      ncSending: false,
      ncSource: null,
      ncMotivo: 1,
      ncLines: [],
      ncOriginalTotal: 0,
      ncMotivos: [
        { value: 1, text: '1 - Anula documento' },
        { value: 2, text: '2 - Corrige texto' },
        { value: 3, text: '3 - Corrige montos' },
      ],
      ncHeaders: [
        { text: 'SKU', value: 'sku', sortable: false },
        { text: 'Descripción', value: 'name', sortable: false },
        { text: 'Cant.', value: 'qty', align: 'end', sortable: false, width: '120px' },
        { text: 'Precio', value: 'price', align: 'end', sortable: false, width: '150px' },
        { text: 'Total', value: 'total', align: 'end', sortable: false, width: '140px' },
      ],
      search: '',
      total: 0,
      payed: 0,
      estatuses: [
        { value: 1, text: 'Emitido', color: 'info' },
        { value: 2, text: 'Recepcionado', color: 'success' },
        { value: 3, text: 'Anulado', color: 'error' },
      ],
      estatusFilterOptions: [
        { text: 'Todos los estatus', value: '' },
        { text: 'Emitidos', value: '1' },
        { text: 'Recepcionados', value: '2' },
        { text: 'Anulados', value: '3' },
      ],
      filter: {
        created_after: moment(new Date(new Date().getFullYear(), new Date().getMonth(), 1)).format('yyyy-MM-DD'),
        created_before: '',
        tipo: '',
        source: '',
        dispatch_batch: '',
        seller: '',
        estatus: '',
      },
      activeTab: 'all',
      sourceOptions: [
        { value: '', text: 'Todos los orígenes' },
        { value: 'simplefactura', text: 'Simple Factura (SII)' },
        { value: 'facturacioncl', text: 'Facturación.cl (SII)' },
        { value: 'local', text: 'Local / Histórico' },
      ],
      headers: [
        { text: 'Documento', align: 'start', value: 'document_id', class: 'table-header', width: '230px' },
        { text: 'Emisión', align: 'start', value: 'date_emited', class: 'table-header', width: '120px' },
        { text: 'Vence', align: 'start', value: 'payment_date', class: 'table-header', width: '120px', sortable: false },
        { text: 'Razón Social / Cliente', align: 'start', value: 'rznSocRecep', class: 'table-header' },
        { text: 'OT', align: 'start', value: 'work_order', class: 'table-header', sortable: true, width: '100px' },
        { text: 'Cot.', align: 'start', value: 'sales_note', class: 'table-header', sortable: false, width: '90px' },
        { text: 'Total Bruto', value: 'get_total', class: 'table-header', align: 'end', sortable: false, width: '130px' },
        { text: 'Pagado', value: 'payed', class: 'table-header', align: 'end', sortable: false, width: '130px' },
        { text: '', align: 'end', value: 'actions', class: 'table-header', sortable: false, width: '130px' },
      ],
    }
  },
  methods: {
    isGuia(item) {
      if (!item) return false
      const tipo = Number(item.sf_tipo || item.tipo)
      return (tipo === 50 || tipo === 52) && Number(item.estatus) !== 3
    },
    facturarGuia(item) {
      const dteId = Number(item.id) > 0 ? item.id : null
      const sfId = item.sf_id || (String(item.id).startsWith('sf-') ? String(item.id).replace('sf-', '') : null)
      const folio = String(item.document_id || item.folio || '').trim()
      this.$router.push({
        name: 'CreateDTE',
        params: { id: -1, tipo: (this.simpleFacturaEnabled || this.facturacionClEnabled) ? 33 : 30 },
        query: {
          from_dte_id: dteId || undefined,
          from_sf_id: sfId || undefined,
          from_guia_id: dteId || sfId || undefined,
          ref_folio: folio || undefined,
          ref_tipo: 52,
          ot: item.work_order || undefined,
        }
      }).catch(() => {})
    },
    isCreditNote(item) {
      if (!item) return false
      const tipo = Number(item.sf_tipo || item.tipo)
      return tipo === 60 || tipo === 61
    },
    getReferenciaDocLabel(item) {
      if (!item) return 'Doc'
      const tipo = item.referencia_tipo
      const tipoNum = Number(tipo)
      if (tipoNum === 33 || tipoNum === 30) return 'Factura'
      if (tipoNum === 39) return 'Boleta'
      if (tipoNum === 52 || tipoNum === 50) return 'Guía'
      if (tipoNum === 61 || tipoNum === 60) return 'NC'
      if (tipoNum === 801) return 'OC'
      if (tipoNum === 802) return 'Nota Pedido'
      if (tipoNum === 803) return 'Contrato'
      if (String(tipo).toUpperCase() === 'HES') return 'HES'
      return 'Doc'
    },
    async openAssociatedNc(nc) {
      if (!nc) return
      if (nc.sf_id) {
        try {
          await downloadDocumentPdf(nc.sf_id)
        } catch (err) {
          notifications.showError(err || 'No se pudo abrir el PDF de la Nota de Crédito')
        }
      } else if (nc.folio || nc.document_id) {
        this.search = String(nc.folio || nc.document_id)
        this.options.page = 1
        this.activeTab = 'nc'
        this.fetch()
      }
    },
    openReferencedDoc(item) {
      if (!item || !item.referencia_folio) return
      this.searchByFolio(item.referencia_folio)
    },
    openRelatedInvoice(inv) {
      if (!inv) return
      const folio = String(inv.folio || inv.document_id || '').trim()
      if (folio) {
        this.searchByFolio(folio)
      }
    },
    searchByFolio(folio) {
      if (!folio) return
      this.search = String(folio).trim()
      this.options.page = 1
      this.activeTab = 'all'
      this.fetch()
    },
    getStatusName(item) {
      const status = this.estatuses.find(i => i.value == item.estatus)
      return status ? status.text : 'Desconocido'
    },
    getStatusColor(item) {
      const status = this.estatuses.find(i => i.value == item.estatus)
      return status ? status.color : 'grey'
    },
    getTypeChipLabel(item) {
      const tipo = Number(item.sf_tipo || item.tipo)
      if (tipo === 33) return 'Factura 33'
      if (tipo === 30) return 'Factura 30'
      if (tipo === 39) return 'Boleta 39'
      if (tipo === 52) return 'Guía 52'
      if (tipo === 50) return 'Guía 50'
      if (tipo === 61) return 'NC 61'
      if (tipo === 60) return 'NC 60'
      return item.tipo_display || `DTE ${tipo}`
    },
    getTypeChipColor(item) {
      const tipo = Number(item.sf_tipo || item.tipo)
      if (tipo === 30 || tipo === 33) return 'primary'
      if (tipo === 39) return 'purple'
      if (tipo === 50 || tipo === 52) return 'info'
      if (tipo === 60 || tipo === 61) return 'error'
      return 'grey'
    },
    onTabChange(tab) {
      this.activeTab = tab
      this.filter.tipo = ''
      this.filter.dispatch_batch = ''
      this.options.page = 1
      this.fetch()
    },
    canIssueCreditNote(item) {
      const isSf = this.simpleFacturaEnabled && item && item.source === 'simplefactura'
      const isFcl = this.facturacionClEnabled && item && item.source === 'facturacioncl'
      if (!isSf && !isFcl) return false
      if (!this.$can('create', 'Invoice')) return false
      const tipo = Number(item.sf_tipo || item.tipo)
      if (tipo !== 33 && tipo !== 52 && tipo !== 30 && tipo !== 50) return false
      if (!item.document_id) return false
      if (!item.work_order && !(isFcl && item.customer)) return false
      return item.estatus !== 3
    },
    mapNcLines(details) {
      return (details || []).map(line => ({
        sku: line.sku || '',
        name: line.name || 'Item',
        qty: Number(line.qty || 0),
        price: Number(line.price || 0),
        unit: line.unit || 'UN',
      }))
    },
    async openCreditNote(item, motivo) {
      this.ncSource = item
      this.ncMotivo = motivo.value
      this.ncLines = this.mapNcLines(item.details)
      if (!this.ncLines.length && (item.sf_id || item.fcl_id)) {
        try {
          const docId = item.fcl_id || item.sf_id
          const doc = item.source === 'facturacioncl' ? await getFclDocument(docId) : await getDocument(docId)
          this.ncLines = this.mapNcLines(doc.detalle)
        } catch (err) {
          notifications.showError('No se pudo cargar el detalle de la factura')
          return
        }
      }
      if (!this.ncLines.length) {
        notifications.showError('La factura no tiene líneas para emitir la nota de crédito')
        return
      }
      this.ncOriginalTotal = this.ncLines.reduce((sum, line) => {
        const neto = Number(line.qty || 0) * Math.round(Number(line.price || 0))
        return sum + neto + Math.round(neto * Number(item.iva || this.$store.getters.getCompany.tax || 19) * 0.01)
      }, 0)
      this.ncDialog = true
    },
    async submitCreditNote() {
      const lines = this.ncLines.filter(line => Number(line.qty || 0) > 0)
      if (!lines.length) {
        notifications.showError('Indique al menos una línea con cantidad')
        return
      }
      if (this.ncMotivo === 3 && this.ncTotal > this.ncOriginalTotal) {
        notifications.showError('El monto de la nota de crédito no puede superar el del documento original')
        return
      }
      this.ncSending = true
      const iva = Number(this.ncSource.iva || this.$store.getters.getCompany.tax || 19)
      const refDate = String(this.ncSource.date_emited || '').slice(0, 10) || moment().format('YYYY-MM-DD')
      try {
        let direccion = null
        if (this.ncSource.sales_note) {
          const salesNote = await getSalesNote(this.ncSource.sales_note)
          const addresses = (salesNote.sales_note_customer && salesNote.sales_note_customer.addresses) || []
          const selected = addresses.find(a => a.default) || addresses[0]
          direccion = selected && selected.id
        }
        const isFclSource = this.ncSource.source === 'facturacioncl'
        const customerId = this.ncSource.customer && this.ncSource.customer.id ? this.ncSource.customer.id : this.ncSource.customer
        if (!direccion && customerId) {
          try {
            const customer = await getCustomer(customerId)
            const addresses = (customer && customer.addresses) || []
            const selected = addresses.find(a => a.default) || addresses[0]
            direccion = selected && selected.id
          } catch (err) {
            direccion = null
          }
        }
        if (!direccion && !isFclSource) {
          notifications.showError('El cliente no tiene dirección. Complétela en el maestro de clientes.')
          return
        }
        const refTipo = Number(this.ncSource.sf_tipo || (this.ncSource.tipo === 30 ? 33 : this.ncSource.tipo === 50 ? 52 : this.ncSource.tipo === 60 ? 61 : this.ncSource.tipo))
        const refFolio = String(this.ncSource.document_id || this.ncSource.folio || '').trim()
        const payload = {
          workorder_id: this.ncSource.work_order || null,
          customer_id: customerId || null,
          tipo: 61,
          direccion,
          neto: this.ncNeto,
          iva,
          observacion: `NC ${this.ncMotivoLabel} · Folio ${refFolio}`,
          fecha: moment().format('YYYY-MM-DD'),
          referencia_tipo: refTipo,
          referencia_folio: refFolio,
          referencia_fecha: refDate,
          motivo: this.ncMotivo,
          razon_ref: this.ncMotivoLabel,
          detalle: lines.map(line => ({
            sku: line.sku,
            name: line.name,
            qty: Number(line.qty || 0),
            price: Math.round(Number(line.price || 0)),
            unit: line.unit || 'UN',
          })),
        }
        let response
        if (this.ncSource.source === 'facturacioncl') {
          response = await createFclDocument(payload)
        } else {
          response = await createDocument(payload)
        }
        if (response.status === 'Ok' || response.folio) {
          notifications.showSuccess(`Nota de crédito emitida. Folio: ${response.folio}`)
          this.ncDialog = false
          if (response.id) {
            try {
              if (this.ncSource.source === 'facturacioncl') {
                await downloadFclDocumentPdf(response.id)
              } else {
                await downloadDocumentPdf(response.id)
              }
            } catch (err) {
              notifications.showError('La NC se emitió, pero no se pudo abrir el PDF')
            }
          }
          await this.fetch()
        } else {
          notifications.showError(response.error || 'No se pudo emitir la nota de crédito')
        }
      } catch (e) {
        const message = typeof e === 'string' ? e : (e && e.error) || 'No se pudo emitir la nota de crédito'
        notifications.showError(message)
      } finally {
        this.ncSending = false
      }
    },
    openDetail(item) {
      this.detailToDisplay = item
      this.showDetail = true
    },
    displayDetails(item) {
      if (item.source === 'simplefactura' && item.sf_id) {
        this.openSimpleFacturaPdf(item)
        return
      }
      if (item.source === 'facturacioncl' && (item.fcl_id || item.sf_id)) {
        this.openFacturacionClPdf(item)
        return
      }
      this.openDetail(item)
    },
    async openSimpleFacturaPdf(item) {
      if (!item.sf_id) return
      this.$set(item, '_pdfLoading', true)
      try {
        await downloadDocumentPdf(item.sf_id)
      } catch (err) {
        notifications.showError(err)
      } finally {
        this.$set(item, '_pdfLoading', false)
      }
    },
    async openFacturacionClPdf(item) {
      const fclId = item.fcl_id || item.sf_id
      if (!fclId) return
      this.$set(item, '_pdfLoading', true)
      try {
        await downloadFclDocumentPdf(fclId)
      } catch (err) {
        notifications.showError(err)
      } finally {
        this.$set(item, '_pdfLoading', false)
      }
    },
    async fetch() {
      this.loading = true
      const { sortBy, sortDesc, page, itemsPerPage } = this.options
      let sellerFilter = this.filter.seller
      if (!this.$can('readAll', 'SalesNote')) {
        sellerFilter = this.$store.getters.getUser.id
      }

      let tipoParam = this.filter.tipo || ''
      if (!tipoParam && this.activeTab && this.activeTab !== 'all') {
        tipoParam = this.activeTab
      }

      let ordering = '-date_emited,-created_at'
      if (sortBy && sortBy.length && sortBy[0]) {
        const isDesc = sortDesc && sortDesc.length ? sortDesc[0] : false
        const prefix = isDesc ? '-' : ''
        if (sortBy[0] === 'date_emited') {
          ordering = `${prefix}date_emited,${prefix}created_at`
        } else {
          ordering = `${prefix}${sortBy[0]}`
        }
      }

      const params = [
        `ordering=${ordering}`,
        `page=${page || 1}`,
        `page_size=${itemsPerPage || 20}`,
        `search=${encodeURIComponent(this.search || '')}`,
        `tipo=${tipoParam || ''}`,
        `source=${this.filter.source || ''}`,
        `emited_after=${this.filter.created_after || ''}`,
        `emited_before=${this.filter.created_before || ''}`,
        `seller=${sellerFilter || ''}`,
        `dispatch_batch=${this.activeTab === 'guias' ? (this.filter.dispatch_batch || '') : ''}`,
        `estatus=${this.filter.estatus || ''}`,
      ]

      try {
        const res = await fetchDTE(params.join('&'))
        this.totalDtes = res.count || 0
        this.dtes = res.results || []
        this.total = res.total || 0
        this.payed = res.payed || 0
      } catch (err) {
        notifications.showError('Error cargando documentos de facturación')
      } finally {
        this.loading = false
      }
    },
    searchTimeOut() {  
      if (this.timer) clearTimeout(this.timer)
      this.timer = setTimeout(() => {
        this.options.page = 1
        this.fetch()
      }, 800)
    },
    async updateEstatus(estatus) {
      try {
        const res = await updateDTE({ id: this.invoice.id, estatus: estatus })
        this.showConfirmCancel = false
        this.showConfirmReceived = false
        this.invoice = ''
        notifications.showSuccess(`Documento #${res.document_id} actualizado con éxito`)
        this.fetch()
      } catch (err) {
        notifications.showError('Error al actualizar el estatus')
      }
    },
  },
  async mounted() {
    this.fetch()
    this.sellers = await fetchSellers()
    if (this.facturacionClEnabled) {
      try {
        this.dispatchBatches = (await listDispatchBatches()) || []
      } catch (e) {
        this.dispatchBatches = []
      }
    }
  },
  computed: {
    dispatchBatchOptions() {
      return [
        { value: '', text: 'Todas' },
        { value: 'masivas', text: 'Solo masivas' },
        { value: 'individuales', text: 'Solo individuales' },
        ...this.dispatchBatches.map(b => ({
          value: String(b.id),
          text: `Lote #${b.id}${b.name ? ' · ' + b.name : ''}`,
        })),
      ]
    },
    computedHeaders() {
      if (this.activeTab === 'nc') {
        return [
          { text: 'Nota de Crédito', align: 'start', value: 'document_id', class: 'table-header', width: '230px' },
          { text: 'Emisión', align: 'start', value: 'date_emited', class: 'table-header', width: '120px' },
          { text: 'Doc. Referenciado', align: 'start', value: 'referencia', class: 'table-header', width: '180px', sortable: false },
          { text: 'Motivo', align: 'start', value: 'motivo_display', class: 'table-header', width: '180px', sortable: false },
          { text: 'Razón Social / Cliente', align: 'start', value: 'rznSocRecep', class: 'table-header' },
          { text: 'OT', align: 'start', value: 'work_order', class: 'table-header', sortable: true, width: '100px' },
          { text: 'Cot.', align: 'start', value: 'sales_note', class: 'table-header', sortable: false, width: '90px' },
          { text: 'Total Bruto', value: 'get_total', class: 'table-header', align: 'end', sortable: false, width: '130px' },
          { text: '', align: 'end', value: 'actions', class: 'table-header', sortable: false, width: '60px' },
        ]
      }
      if (this.activeTab === 'guias') {
        return [
          { text: 'Guía de Despacho', align: 'start', value: 'document_id', class: 'table-header', width: '230px' },
          { text: 'Emisión', align: 'start', value: 'date_emited', class: 'table-header', width: '120px' },
          { text: 'Factura(s) Relacionada(s)', align: 'start', value: 'related_invoices', class: 'table-header', width: '220px', sortable: false },
          { text: 'Razón Social / Cliente', align: 'start', value: 'rznSocRecep', class: 'table-header' },
          { text: 'OT', align: 'start', value: 'work_order', class: 'table-header', sortable: true, width: '100px' },
          { text: 'Cot.', align: 'start', value: 'sales_note', class: 'table-header', sortable: false, width: '90px' },
          { text: 'Total Bruto', value: 'get_total', class: 'table-header', align: 'end', sortable: false, width: '130px' },
          { text: '', align: 'end', value: 'actions', class: 'table-header', sortable: false, width: '150px' },
        ]
      }
      if (this.activeTab === 'facturas') {
        return [
          { text: 'Factura', align: 'start', value: 'document_id', class: 'table-header', width: '230px' },
          { text: 'Emisión', align: 'start', value: 'date_emited', class: 'table-header', width: '120px' },
          { text: 'Doc. Referenciado', align: 'start', value: 'referencia', class: 'table-header', width: '190px', sortable: false },
          { text: 'Vence', align: 'start', value: 'payment_date', class: 'table-header', width: '120px', sortable: false },
          { text: 'Razón Social / Cliente', align: 'start', value: 'rznSocRecep', class: 'table-header' },
          { text: 'OT', align: 'start', value: 'work_order', class: 'table-header', sortable: true, width: '100px' },
          { text: 'Cot.', align: 'start', value: 'sales_note', class: 'table-header', sortable: false, width: '90px' },
          { text: 'Total Bruto', value: 'get_total', class: 'table-header', align: 'end', sortable: false, width: '130px' },
          { text: 'Pagado', value: 'payed', class: 'table-header', align: 'end', sortable: false, width: '130px' },
          { text: '', align: 'end', value: 'actions', class: 'table-header', sortable: false, width: '130px' },
        ]
      }
      return this.headers
    },
    simpleFacturaEnabled() {
      return !!this.$store.getters.getCompany?.simplefactura_integration_enabled
    },
    facturacionClEnabled() {
      return !!this.$store.getters.getCompany?.facturacioncl_integration_enabled
    },
    tipoOptions() {
      if (this.activeTab === 'facturas') {
        return [
          { value: '', text: 'Todas las facturas' },
          { value: '33', text: 'Factura Electrónica (33)' },
          { value: '30', text: 'Factura Afecta / Manual (30)' },
        ]
      }
      if (this.activeTab === 'boletas') {
        return [
          { value: '', text: 'Todas las boletas' },
          { value: '39', text: 'Boleta Electrónica (39)' },
        ]
      }
      if (this.activeTab === 'guias') {
        return [
          { value: '', text: 'Todas las guías' },
          { value: '52', text: 'Guía de Despacho Electrónica (52)' },
          { value: '50', text: 'Guía de Despacho Manual (50)' },
        ]
      }
      if (this.activeTab === 'nc') {
        return [
          { value: '', text: 'Todas las notas de crédito' },
          { value: '61', text: 'Nota de Crédito Electrónica (61)' },
          { value: '60', text: 'Nota de Crédito Manual (60)' },
        ]
      }
      return [
        { value: '', text: 'Todos los tipos' },
        { value: 'facturas', text: 'Facturas (30/33)' },
        { value: 'boletas', text: 'Boletas (39)' },
        { value: 'guias', text: 'Guías de Despacho (50/52)' },
        { value: 'nc', text: 'Notas de Crédito (60/61)' },
      ]
    },
    ncMotivoLabel() {
      const found = this.ncMotivos.find(item => item.value === this.ncMotivo)
      return found ? found.text : 'Nota de crédito'
    },
    ncNeto() {
      return this.ncLines.reduce((sum, line) => sum + Number(line.qty || 0) * Math.round(Number(line.price || 0)), 0)
    },
    ncIvaAmount() {
      const iva = Number((this.ncSource && this.ncSource.iva) || this.$store.getters.getCompany.tax || 19)
      return Math.round(this.ncNeto * iva * 0.01)
    },
    ncTotal() {
      return this.ncNeto + this.ncIvaAmount
    },
  }
}

</script>

<style scoped>
.modern-master-view { background-color: #f4f7f9; min-height: 100vh; }
.modern-card { border-radius: 12px !important; border: 1px solid rgba(0,0,0,0.05) !important; background-color: white !important; }
.shadow-sm { box-shadow: 0 4px 12px rgba(0,0,0,0.04) !important; }
.border-white-sm { border: 2px solid white !important; }
.border-bottom { border-bottom: 1px solid rgba(0,0,0,0.05) !important; }
.border-left-primary { border-left: 4px solid var(--v-primary-base) !important; }
.border-left-success { border-left: 4px solid var(--v-success-base) !important; }
.bg-lightgrey { background-color: #f8fbfc; }
.lh-1 { line-height: 1 !important; }
.lh-1-2 { line-height: 1.2 !important; }
.gap-3 { gap: 12px; }
.uppercase { text-transform: uppercase; }
.tracking-widest { letter-spacing: 1.8px; }
.tracking-tighter { letter-spacing: -0.5px; }
.h-44 { height: 44px !important; }
.opacity-60 { opacity: 0.6; }

.modern-input >>> fieldset { border-radius: 8px !important; border-color: #e0e4e8 !important; }
.modern-search-field { width: 320px; }
.modern-search-field >>> fieldset { border-radius: 24px !important; border-color: #e0e4e8 !important; }

.modern-table >>> thead th { background-color: #f8fbfc !important; height: 56px !important; text-transform: uppercase; font-size: 0.75rem !important; letter-spacing: 1px; font-weight: 900 !important; color: #607d8b !important; }
.modern-table >>> td { height: 72px !important; border-bottom: 1px solid #f0f3f5 !important; }
.modern-table >>> tr:hover { background-color: #f4f7f9 !important; cursor: pointer; }

.border-subtle { border: 1px solid rgba(0,0,0,0.06) !important; }
.modern-tabs >>> .v-tab {
  text-transform: none !important;
  font-size: 0.85rem !important;
  font-weight: 800 !important;
  letter-spacing: 0.3px;
  min-height: 48px;
}
.modern-tabs >>> .v-tab--active {
  color: var(--v-primary-base) !important;
}

.row-style-nc { background-color: #fffbfa !important; }
.row-style-nc td { color: #d32f2f !important; }
.row-style-annulled { background-color: #fff5f5 !important; }
.row-style-annulled td { color: #424242 !important; }
.row-style-annulled:hover { background-color: #fee2e2 !important; }
.cursor-pointer { cursor: pointer; }
.hover-link:hover { text-decoration: underline; }
.bg-white { background-color: white !important; }
</style>